// src/utils/preload.ts
// SW-PRELOAD (19.09.2026), часть 3 из 3: явная предзагрузка данных ВСЕХ
// доступных курсов на PRELOAD_WINDOW_DAYS дней вперёд. Снимает зависимость
// офлайна от того, по каким экранам человек фактически ходил (находка
// SW-FALLBACK-RECON: воркер кэширует под точным ключом только то, что
// реально запрашивалось; тексты/прогресс/бейджи/артефакты/медиа, которые
// человек не открыл сам, до этого модуля не попадали ни в IndexedDB, ни в
// Cache Storage — SW-STRATEGIES/SW-MIGRATE закрыли только "как отдаётся уже
// закэшированное", не "как оно там оказывается").
//
// content-access, инвариант 8: окно предзагрузки обязано совпадать с
// грейсом офлайна, иначе они разойдутся. Грейс закреплён в одном месте —
// pwa/src/utils/store.ts (STALE_AFTER_MS) — отсюда и берётся число, не
// подбирается заново. Серверное зеркало той же величины — PRELOAD_WINDOW_DAYS
// в api/index.js (по умолчанию тоже 3, трек PRELOAD-WINDOW, 18.09.2026).
//
// tenant-isolation: этот модуль не выбирает клиента и не читает Host/Origin —
// он работает над снапшотом, который App.tsx уже получил через штатный
// гейт (client_gate, resolveUserResult), тем самым уже отфильтрованным по
// арендатору текущей сессии (tenant-isolation, инвариант про единственный
// рубеж — имя cookie; здесь мы ниже этого рубежа, а не рядом с ним).
//
// Все сетевые чтения курсовых данных (settings/progress/badges/artifacts/
// content) идут через уже существующие модули api/* и
// utils/energy-reset/settings.ts — они сами кладут результат в IndexedDB
// (store.ts). Этот модуль не открывает свой путь записи, только вызывает их.

import { findEnrollment, type AccessSnapshot } from "../api/snapshot";
import { getCourseSettingsByCourseId } from "./energy-reset/settings";
import { getDays } from "../api/progress";
import { loadBadgesCatalog, loadAwarded } from "../api/badges";
import { fetchArtifacts } from "../api/artifacts";
import { getContentDay, mediaUrl } from "../api/content";
import { STALE_AFTER_MS } from "./store";

// Окно предзагрузки = грейс офлайна, одна константа на клиенте и сервере
// (content-access, инвариант 8). Не 3 в лоб — вычислено из STALE_AFTER_MS.
const PRELOAD_WINDOW_DAYS = Math.round(STALE_AFTER_MS / (24 * 60 * 60 * 1000));

// Потолок медиа за сессию предзагрузки. Тексты/настройки/прогресс/бейджи/
// артефакты в потолок не входят — они мелкие (решение задания SW-PRELOAD).
const MEDIA_CEILING_MB = 200;
const MEDIA_CEILING_BYTES = MEDIA_CEILING_MB * 1024 * 1024;

// Один раз за сессию приложения — не по таймеру, не повторно при повторном
// вызове (App.tsx может дёрнуть эффект больше одного раза за жизнь вкладки).
let started = false;

type Budget = {
  usedBytes: number;
  ceilingReached: boolean;
};

/**
 * Курс, который сейчас открыт (URL /dashboard/:id или /course/:id) —
 * единственный доступный на клиенте сигнал "последний открытый курс": в
 * приложении нет отдельного персистентного хранилища для этого (проверено
 * чтением App.tsx/DashboardScreen.tsx/CourseScreen.tsx — записи такого рода
 * нет). Если адрес ничего не говорит о курсе — считаем "неизвестно" и не
 * переставляем порядок, как и просит задание ("если известен").
 */
function lastOpenedCourseId(): number | null {
  const m = window.location.pathname.match(/^\/(?:dashboard|course)\/(\d+)/);
  if (!m) return null;
  const id = Number(m[1]);
  return Number.isFinite(id) ? id : null;
}

function orderCourseIds(courseIds: number[]): number[] {
  const last = lastOpenedCourseId();
  if (last === null || !courseIds.includes(last)) return courseIds;
  return [last, ...courseIds.filter((id) => id !== last)];
}

/** Тянет один медиафайл целиком (не Range-кусками — офлайн не должен проигрывать только начало). Молча пропускает отказ конкретного файла, не роняет остальную предзагрузку. */
async function preloadMedia(rel: string | null, budget: Budget): Promise<void> {
  if (!rel || budget.ceilingReached) return;
  const url = mediaUrl(rel);
  if (!url) return;
  try {
    const res = await fetch(url);
    if (!res.ok) return;
    // Воркер (CacheFirst /api/media/:uuid, sw.ts) перехватывает этот fetch и
    // сам кладёт ответ в кэш "media" по uuid — здесь читаем только объём.
    const blob = await res.blob();
    budget.usedBytes += blob.size;
    if (budget.usedBytes >= MEDIA_CEILING_BYTES) {
      budget.ceilingReached = true;
    }
  } catch (e) {
    console.log(`[preload] media fetch failed, skipped: ${e instanceof Error ? e.message : String(e)}`);
  }
}

/** Один день курса: сначала текст, потом медиа — при обрыве связи человек получит целый день текста, не три половинчатых медиа. */
async function preloadDay(courseId: number, day: number, budget: Budget): Promise<void> {
  if (budget.ceilingReached) return;
  const content = await getContentDay(courseId, day); // сам кладёт текст в IndexedDB (content.ts)
  if (budget.ceilingReached) return; // защита от гонки: соседний путь уже упёрся в потолок, пока этот await шёл
  await preloadMedia(content.media?.audio_url ?? null, budget);
  await preloadMedia(content.media?.video_url ?? null, budget);
}

/** Один курс: настройки/прогресс/бейджи/артефакты, затем дни окна currentDay..currentDay+PRELOAD_WINDOW_DAYS. */
async function preloadCourse(courseId: number, currentDay: number, budget: Budget): Promise<void> {
  if (budget.ceilingReached) return;

  const [settingsResult] = await Promise.all([
    getCourseSettingsByCourseId(String(courseId)),
    getDays(courseId),
    loadBadgesCatalog(courseId),
    loadAwarded(courseId),
    fetchArtifacts(courseId),
  ]);

  // Курс завершён (day > duration_days) не предзагружаем дальше его конца —
  // сервер всё равно ответит day_out_of_range (легитимно пусто), но нет
  // смысла тратить запросы и место под дни, которых у курса не существует.
  const durationDays =
    settingsResult.status === "ok" || settingsResult.status === "cached"
      ? Number(settingsResult.data.duration_days ?? 0)
      : 0;

  const startDay = Math.max(1, currentDay);
  let endDay = currentDay + PRELOAD_WINDOW_DAYS;
  if (durationDays > 0 && endDay > durationDays) endDay = durationDays;

  for (let day = startDay; day <= endDay; day++) {
    if (budget.ceilingReached) break;
    await preloadDay(courseId, day, budget);
  }
}

/**
 * Запускает предзагрузку всех курсов из снапшота. Вызывается из App.tsx
 * сразу после успешного getSnapshot() (status === "ok") — снапшот уже под
 * рукой у вызывающей стороны, второй раз здесь не запрашивается. Не ждать
 * результата на стороне вызова — работа идёт фоном, интерфейс не блокирует.
 */
export async function runPreload(snapshot: AccessSnapshot): Promise<void> {
  if (started) return;
  started = true;

  // Офлайн — не запускаем вовсе, молча (задание, Шаг 3).
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;

  try {
    const courseIds = orderCourseIds([...snapshot.courses]);
    if (courseIds.length === 0) return;

    console.log(`[preload] start, courses=${courseIds.length}, window_days=${PRELOAD_WINDOW_DAYS}, ceiling_mb=${MEDIA_CEILING_MB}`);

    const budget: Budget = { usedBytes: 0, ceilingReached: false };

    for (const courseId of courseIds) {
      if (budget.ceilingReached) break;
      const enr = findEnrollment(snapshot, courseId);
      const currentDay = enr?.current_day ?? 1;
      await preloadCourse(courseId, currentDay, budget);
      if (!budget.ceilingReached) {
        console.log(`[preload] course=${courseId} done`);
      }
    }

    if (budget.ceilingReached) {
      console.log(`[preload] ceiling reached (${MEDIA_CEILING_MB}MB), stopped; downloaded_mb=${(budget.usedBytes / (1024 * 1024)).toFixed(1)}`);
    }
    console.log(`[preload] finished, total_media_mb=${(budget.usedBytes / (1024 * 1024)).toFixed(1)}`);
  } catch (e) {
    console.error("[preload] unexpected error:", e instanceof Error ? e.message : String(e));
  }
}
